/**
 * 助成金支払日登録 API（/api/subsidy-payments）。F-EXP-02 / S-51
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { expenseKeys } from '../common/api';

export const SUBSIDY_TARGETS = ['賃金・用船料', '燃油', '潤滑油', '現像代'] as const;
export type SubsidyTarget = (typeof SUBSIDY_TARGETS)[number];

export interface SubsidyRowDto {
  id: number;
  voyageReportId: number;
  vesselId: number;
  vesselName: string;
  registrationNo: string;
  captainName: string;
  periodFrom: string;
  periodTo: string;
  crewCount: number;
  tonnage: number;
  surveyDate: string | null;
  surveyHours: number | null;
  wageTotal: number | null;
  charterTotal: number | null;
  amount: number | null;
  refDate: string | null;
  subsidyScheduledOn: string | null;
  subsidyPaidOn: string | null;
}

export interface SubsidyFilter {
  businessOperatorId?: number | null;
  target: SubsidyTarget;
  vesselName?: string;
  registrationNo?: string;
  departureFrom?: string;
  departureTo?: string;
  surveyFrom?: string;
  surveyTo?: string;
  scheduledFrom?: string;
  scheduledTo?: string;
  paidFrom?: string;
  paidTo?: string;
  scheduled?: '予定済' | '未予定' | '両方';
  paid?: '支払済' | '未支払' | '両方';
}

export const subsidyKeys = {
  all: [...expenseKeys.all, 'subsidy'] as const,
  list: (year: number, f: SubsidyFilter) => [...subsidyKeys.all, 'list', year, f] as const,
};

export function useSubsidyPayments(year: number, filter: SubsidyFilter, enabled: boolean) {
  return useQuery({
    queryKey: subsidyKeys.list(year, filter),
    queryFn: ({ signal }) => api.get<{ items: SubsidyRowDto[]; total: number }>('/subsidy-payments', { fiscalYear: year, ...filter }, { signal }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useSaveSubsidyPayments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { target: SubsidyTarget; items: { id: number; subsidyScheduledOn: string | null; subsidyPaidOn?: string | null }[] }) =>
      api.put<{ updated: number }>('/subsidy-payments', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: expenseKeys.all }),
  });
}
